"use client";
import Image, { ImageProps } from 'next/image';
import React, { useState, useEffect, useMemo } from 'react';

// Runtime-loaded blur manifest (optional). If not present, we fallback to empty placeholder.
let blurManifest: Record<string,string> | null = null;
async function loadManifest() {
  if (blurManifest) return blurManifest;
  try {
    const res = await fetch('/blur-placeholders.json');
    if (res.ok) {
      blurManifest = await res.json();
    } else {
      blurManifest = {};
    }
  } catch {
    blurManifest = {};
  }
  return blurManifest;
}

interface OptimizedImageProps extends Omit<ImageProps,'placeholder'|'blurDataURL'> {
  fallbackClassName?: string;
  fallbackContent?: React.ReactNode;
}

export function OptimizedImage({ src, alt, fallbackClassName, fallbackContent, ...rest }: OptimizedImageProps) {
  const [error, setError] = useState(false);
  const [blurDataURL, setBlur] = useState<string | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    loadManifest().then(m => {
      if (!mounted || !m) return;
      if (typeof src === 'string' && m[src]) setBlur(m[src]);
    });
    return () => { mounted = false; };
  }, [src]);

  const placeholder = useMemo(() => blurDataURL ? 'blur' : 'empty', [blurDataURL]);

  if (error) {
    return (
      <div className={fallbackClassName || 'bg-gray-100 flex items-center justify-center text-xs text-gray-500'}>
        {fallbackContent || 'Immagine non disponibile'}
      </div>
    );
  }

  return (
    <Image 
      src={src}
      alt={alt}
      onError={() => setError(true)}
      placeholder={placeholder as any}
      blurDataURL={blurDataURL}
      {...rest}
    />
  );
}
